import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { cityService } from '../../services/cityService';

const CityList = ({ onEdit }) => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

 

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const response = await cityService.getCities();
      const citiesData = response.cities || response;
      // Normalize list shape from API and keep freezone counts accurate.
      const normalizedCities = Array.isArray(citiesData) ? citiesData.map(city => ({
        ...city,
        benefits: city.benefits || [],
        freezones: city.freezones || [],
        freezones_count: Number.isFinite(city.freezones_count)
          ? city.freezones_count
          : Array.isArray(city.freezones)
            ? city.freezones.length
            : 0
      })) : [];
      
      setCities(normalizedCities);
    } catch (error) {
      toast.error('Failed to load cities from server, showing sample data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cityId) => {
    if (window.confirm('Are you sure you want to delete this city?')) {
      try {
        await cityService.deleteCity(cityId);
        setCities(cities.filter(city => city.id !== cityId));
        toast.success('City deleted successfully');
      } catch (error) {
        toast.error('Failed to delete city');
      }
    }
  };

  const handleStatusToggle = async (cityId) => {
    try {
      const city = cities.find(c => c.id === cityId);
      const newStatus = city.status === 'active' ? 'inactive' : 'active';
      
      await cityService.updateCityStatus(cityId, newStatus);
      setCities(cities.map(city => 
        city.id === cityId 
          ? { ...city, status: newStatus }
          : city
      ));
      toast.success('City status updated successfully');
    } catch (error) {
      toast.error('Failed to update city status');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Cities ({(cities || []).length})</h3>
      </div>
      
      <div className="divide-y divide-gray-200">
        {(cities || []).map((city) => {
          const freezoneNames = (city.freezones || [])
            .map((freezone) => (typeof freezone === 'string' ? freezone : freezone?.name))
            .filter(Boolean);

          return (
          <div key={city.id} className="p-6 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div 
                className="flex items-center space-x-4 flex-1 cursor-pointer"
                onClick={() => navigate(`/cities/${city.id}`)}
              >
                <div className="flex-shrink-0">
                  <img
                    className="h-16 w-16 rounded-lg object-cover"
                    src={city.image_url}
                    alt={city.name}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/64x64?text=' + city.name.charAt(0);
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-lg font-medium text-gray-900 hover:text-blue-600">{city.name}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      city.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {city.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {(city.benefits || []).length} benefits • {city.freezones_count || 0} freezones
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {freezoneNames.map((freezone, index) => (
                      <span
                        key={`${city.id}-fz-${index}`}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {freezone}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center text-sm text-blue-600">
                    <BuildingOfficeIcon className="w-4 h-4 mr-1" />
                    Click to view freezones
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onEdit(city)}
                  className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleStatusToggle(city.id)}
                  className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    city.status === 'active'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {city.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(city.id)}
                  className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="mt-4">
              <h5 className="text-sm font-medium text-gray-900 mb-2">Key Benefits:</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                {(city.benefits || []).slice(0, 3).map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    {benefit}
                  </li>
                ))}
                {(city.benefits || []).length > 3 && (
                  <li className="text-blue-600 text-sm">
                    +{(city.benefits || []).length - 3} more benefits
                  </li>
                )}
              </ul>
            </div>
          </div>
          );
        })}
      </div>
      
      {cities.length === 0 && (
        <div className="p-6 text-center">
          <p className="text-gray-500">No cities found. Create your first city to get started.</p>
        </div>
      )}
    </div>
  );
};

export default CityList; 
